import { isAxiosError } from 'axios';
import type {
  ApiSuccess,
  IntegrityApp,
  IntegrityNonceResult,
  IntegrityVerifyInput,
  IntegrityVerifyResult,
} from '@capsico/contracts';
import { nativeIntegrityPlatform, playIntegrityToken } from '../native/integrity';
import { api } from './client';
import {
  passRemainingMs,
  setIntegrityPass,
  setIntegrityRefresher,
  setIntegrityStatus,
  type IntegrityStatus,
} from './integrityState';

/*
 * W24 app integrity for the customer app: nonce → Play Integrity token → verify → signed pass, on
 * start, every 6 hours and when a sensitive call is refused with "integrity_required".
 * A full no-op when the server has integrity off; never blocks the app itself — only enforce mode
 * refuses sensitive calls, and the gate then explains how to get the official app.
 */

const APP: IntegrityApp = 'customer';
const REFRESH_MS = 6 * 60 * 60_000;
/** Refresh a little before the pass runs out. */
const EARLY_MS = 10 * 60_000;

let inFlight: Promise<boolean> | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let started = false;

async function fetchNonce(platform: 'android' | 'ios'): Promise<IntegrityNonceResult | null> {
  try {
    const res = await api.post<ApiSuccess<IntegrityNonceResult>>('/v1/integrity/nonce', {
      app: APP,
      platform,
    });
    return res.data.data;
  } catch (error) {
    // 404: the integrity feature is not deployed / switched off on the server.
    if (isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

async function verify(input: IntegrityVerifyInput): Promise<IntegrityVerifyResult> {
  const res = await api.post<ApiSuccess<IntegrityVerifyResult>>('/v1/integrity/verify', input);
  return res.data.data;
}

function schedule(ms: number): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void refreshIntegrity(), Math.max(60_000, ms));
}

async function run(): Promise<boolean> {
  const platform = nativeIntegrityPlatform();
  if (!platform) {
    setIntegrityStatus('unsupported');
    return false;
  }
  let nonce: IntegrityNonceResult | null;
  try {
    nonce = await fetchNonce(platform);
  } catch {
    // Offline or the server is busy: try again later, keep any pass we still have.
    schedule(5 * 60_000);
    return passRemainingMs() > 0;
  }
  if (!nonce || nonce.mode === 'off' || !nonce.nonce) {
    setIntegrityPass(null, null);
    setIntegrityStatus('off');
    schedule(REFRESH_MS);
    return false;
  }
  if (platform === 'ios' || !nonce.platformActive || !nonce.cloudProjectNumber) {
    // iOS App Attest is dormant; without a Play project number there is nothing to ask Google.
    setIntegrityStatus('unsupported');
    schedule(REFRESH_MS);
    return false;
  }
  let token: string;
  try {
    token = await playIntegrityToken(nonce.nonce, nonce.cloudProjectNumber);
  } catch {
    // No Play services (sideloaded on a de-Googled phone, emulator, …).
    setIntegrityPass(null, null);
    setIntegrityStatus(nonce.mode === 'enforce' ? 'failed' : 'ok');
    schedule(REFRESH_MS);
    return false;
  }
  try {
    const result = await verify({ platform: 'android', app: APP, nonce: nonce.nonce, token });
    setIntegrityPass(result.pass, result.expiresAt);
    const passed = result.pass !== null;
    setIntegrityStatus(passed || result.mode !== 'enforce' ? 'ok' : 'failed');
    schedule(passed ? Math.min(REFRESH_MS, passRemainingMs() - EARLY_MS) : REFRESH_MS);
    return passed;
  } catch {
    schedule(5 * 60_000);
    return false;
  }
}

/** One check at a time; callers share the result. */
export function refreshIntegrity(): Promise<boolean> {
  inFlight ??= run().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Starts the check once per app launch and lets the HTTP client ask for a fresh pass on 403. */
export function startIntegrity(): void {
  if (started) return;
  started = true;
  setIntegrityRefresher(() => refreshIntegrity());
  void refreshIntegrity();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && passRemainingMs() < EARLY_MS) {
      void refreshIntegrity();
    }
  });
}

export type { IntegrityStatus };
